import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { userRequest } from "../../utils/requestMethods";
import { useStateContext } from "../../contexts/ContextProvider";
import {
  MobileFriendlyNotification,
  SkeletonTable,
  ClockAttemptsModal,
  StaffLatenessPenaltiesModal,
  StaffClockInSettingsModal,
} from "../../components";
import DataTable from "react-data-table-component";
import {
  FiCalendar,
  FiUsers,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiX,
  FiSquare,
  FiSmartphone,
  FiRefreshCw,
  FiSearch,
  FiDownload,
  FiList,
  FiAlertCircle,
  FiSettings,
} from "react-icons/fi";
import { format } from "date-fns";
import { generateSchoolSessions } from "../../utils/schoolYearUtils";
import { BsQrCode } from "react-icons/bs";

// Attendance status codes from the backend: 1 present, 2 late, 0 absent.
const STATUS_META = {
  1: { key: "present", label: "Present", badge: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  2: { key: "late", label: "Late", badge: "bg-amber-50 text-amber-700 ring-amber-200" },
  0: { key: "absent", label: "Absent", badge: "bg-rose-50 text-rose-700 ring-rose-200" },
};
const NOT_MARKED = {
  key: "not_marked",
  label: "Not marked",
  badge: "bg-slate-100 text-slate-600 ring-slate-200",
};
const statusMeta = (status) => STATUS_META[status] || NOT_MARKED;

const formatTime = (value) => (value?.time ? format(new Date(value.time), "hh:mm a") : null);

// Time between clock in and clock out, e.g. "7h 45m".
const formatWorked = (record) => {
  if (!record?.clock_in?.time || !record?.clock_out?.time) return null;
  const minutes = Math.round(
    (new Date(record.clock_out.time) - new Date(record.clock_in.time)) / 60000,
  );
  if (minutes <= 0) return null;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

const formatRecordDate = (record) =>
  record?.date ? format(new Date(record.date), "EEE, dd MMM yyyy") : "-";

const initials = (row) =>
  `${row.firstName?.[0] || ""}${row.lastName?.[0] || ""}`.toUpperCase() || "?";

// Counts for a set of rows, by status.
const summarize = (rows) => {
  const counts = { present: 0, late: 0, absent: 0, not_marked: 0 };
  rows.forEach((row) => {
    counts[statusMeta(row.record?.status).key] += 1;
  });
  return counts;
};

const TeacherAttendance = () => {
  const { school, refreshSchool } = useStateContext();
  const history = useHistory();
  const [teacherAttendance, setTeacherAttendance] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showClockAttemptsModal, setShowClockAttemptsModal] = useState(false);
  const [showPenaltiesModal, setShowPenaltiesModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // 'all' | 'present' | 'late' | 'absent'
  const [showIndividualModal, setShowIndividualModal] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [individualAttendance, setIndividualAttendance] = useState([]);
  const [individualLoading, setIndividualLoading] = useState(false);
  const [filters, setFilters] = useState({
    session: school?.settings?.current_session || "2024/2025",
    term: school?.settings?.current_term || "1",
    from: format(new Date(), "yyyy-MM-dd"),
    to: format(new Date(), "yyyy-MM-dd"),
  });
  const [individualFilters, setIndividualFilters] = useState({
    session: school?.settings?.current_session || "2024/2025",
    term: school?.settings?.current_term || "1",
    from: format(new Date(), "yyyy-MM-dd"),
    to: format(new Date(), "yyyy-MM-dd"),
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [qrLoading, setQrLoading] = useState(false);
  const [qrSuccess, setQrSuccess] = useState("");
  const [qrError, setQrError] = useState("");
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [brandedQRPreview, setBrandedQRPreview] = useState(null);

  const schoolId = school?._id;
  const schoolTerm = school?.settings?.current_term;
  const schoolSession = school?.settings?.current_session;

  // Generate school sessions for dropdowns
  const schoolSessions = generateSchoolSessions(2022, 20);

  // Update filters when school settings change
  useEffect(() => {
    if (schoolTerm && schoolSession) {
      setFilters((prev) => ({
        ...prev,
        session: schoolSession,
        term: schoolTerm,
      }));
      setIndividualFilters((prev) => ({
        ...prev,
        session: schoolSession,
        term: schoolTerm,
      }));
    }
  }, [schoolTerm, schoolSession]);

  const fetchTeacherAttendance = async (customFilters = null) => {
    if (!schoolId) return;

    try {
      setLoading(true);
      const filterParams = customFilters || filters;

      let endpoint = `/users/staff-attendance?school_id=${schoolId}&limit=100&session=${encodeURIComponent(filterParams.session)}&term=${filterParams.term}&from=${encodeURIComponent(filterParams.from)}&to=${encodeURIComponent(filterParams.to)}`;

      const { data } = await userRequest.get(endpoint);
      const attendanceData = data?.data || [];

      // Flatten the data structure to show each attendance record as a separate row
      const flattenedData = [];
      if (Array.isArray(attendanceData)) {
        attendanceData.forEach((teacher) => {
          if (teacher.records && Array.isArray(teacher.records)) {
            teacher.records.forEach((record) => {
              flattenedData.push({
                userId: teacher.userId,
                firstName: teacher.firstName,
                lastName: teacher.lastName,
                record: record,
                // Add a unique ID for DataTable
                uniqueId: `${teacher.userId}_${record.date}`,
              });
            });
          }
        });
      }

      setTeacherAttendance(flattenedData);
    } catch (error) {
      console.log(error);
      setError(error.response?.data || "Failed to fetch attendance data.");
      setTeacherAttendance([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchIndividualTeacherAttendance = async (teacherId, customFilters = null) => {
    if (!schoolId || !teacherId) return;

    try {
      setIndividualLoading(true);
      const filterParams = customFilters || individualFilters;

      let endpoint = `/users/staff-attendance?school_id=${schoolId}&usersId=${teacherId}&limit=100&session=${encodeURIComponent(filterParams.session)}&term=${filterParams.term}&from=${encodeURIComponent(filterParams.from)}&to=${encodeURIComponent(filterParams.to)}`;

      const { data } = await userRequest.get(endpoint);
      const attendanceData = data?.data || [];

      // Flatten the data structure for individual teacher modal as well
      const flattenedData = [];
      if (Array.isArray(attendanceData)) {
        attendanceData.forEach((teacher) => {
          if (teacher.records && Array.isArray(teacher.records)) {
            teacher.records.forEach((record) => {
              flattenedData.push({
                userId: teacher.userId,
                firstName: teacher.firstName,
                lastName: teacher.lastName,
                record: record,
                // Add a unique ID for DataTable
                uniqueId: `${teacher.userId}_${record.date}`,
              });
            });
          }
        });
      }

      setIndividualAttendance(flattenedData);
    } catch (error) {
      console.log(error);
      setError(error.response?.data || "Failed to fetch individual teacher attendance data.");
      setIndividualAttendance([]);
    } finally {
      setIndividualLoading(false);
    }
  };

  useEffect(() => {
    if (schoolId && schoolTerm && schoolSession) {
      fetchTeacherAttendance();
    }
  }, [schoolId, schoolTerm, schoolSession]);

  const handleDirectFilterChange = (field, value) => {
    const updatedFilters = {
      ...filters,
      [field]: value,
    };
    // Keep the range valid: moving one end past the other moves both.
    if (field === "from" && value > updatedFilters.to) updatedFilters.to = value;
    if (field === "to" && value < updatedFilters.from) updatedFilters.from = value;
    setFilters(updatedFilters);
    setIndividualFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    fetchTeacherAttendance(updatedFilters);
  };

  const handleShowToday = () => {
    const today = format(new Date(), "yyyy-MM-dd");
    const updatedFilters = { ...filters, from: today, to: today };
    setFilters(updatedFilters);
    setIndividualFilters((prev) => ({ ...prev, from: today, to: today }));
    fetchTeacherAttendance(updatedFilters);
  };

  const handleViewIndividualAttendance = (teacher) => {
    if (teacher && teacher.userId) {
      setSelectedTeacher(teacher);
      setShowIndividualModal(true);
      // Fetch individual attendance with current filters
      fetchIndividualTeacherAttendance(teacher.userId, individualFilters);
    } else {
      setError("Invalid teacher data. Please try again.");
    }
  };

  const handleCloseIndividualModal = () => {
    setShowIndividualModal(false);
    setSelectedTeacher(null);
    setIndividualAttendance([]);
  };

  const handleIndividualFilterChange = (field, value) => {
    setIndividualFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleApplyIndividualFilters = () => {
    if (selectedTeacher) {
      fetchIndividualTeacherAttendance(selectedTeacher.userId, individualFilters);
    }
  };

  // Generate QR code for teacher attendance
  const handleGenerateQRCode = async () => {
    if (!schoolId) {
      setQrError("School ID not found");
      return;
    }

    try {
      setQrLoading(true);
      setQrError("");
      setQrSuccess("");

      const { data } = await userRequest.post(`/schools/${schoolId}/generate-qr`, {
        type: "teacher_attendance",
      });
      //console.log("QR Code generated:", data);
      // Handle simple string response - create QR code URL from the hash
      const qrHash = data;
      const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrHash)}`;

      // Store QR code data and show modal
      setQrCodeData({
        qrCodeUrl: qrCodeUrl,
        qrHash: qrHash,
        generatedAt: new Date().toISOString(),
        schoolId: schoolId,
      });
      setShowQRModal(true);
      setQrSuccess("QR code generated successfully! Teachers can now use this for clock-in/out.");

      // Generate preview of branded QR code. Pass the URL directly: qrCodeData state
      // isn't updated yet at this point.
      try {
        const preview = await generateBrandedQRCode(qrCodeUrl);
        setBrandedQRPreview(preview);
      } catch (error) {
        console.error("Error generating preview:", error);
      }
    } catch (error) {
      console.error("Error generating QR code:", error);
      setQrError(error.response?.data?.message || "Failed to generate QR code");
    } finally {
      setQrLoading(false);
    }
  };

  // Generate branded QR code with school information
  const generateBrandedQRCode = async (qrCodeUrl = qrCodeData?.qrCodeUrl) => {
    if (!qrCodeUrl || !school) return null;

    return new Promise((resolve, reject) => {
      // Create canvas
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // Set canvas size (A4-like proportions for better printing)
      canvas.width = 600;
      canvas.height = 800;

      // Background
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Load QR code image
      const qrImage = new Image();
      qrImage.crossOrigin = "anonymous";
      qrImage.onload = () => {
        try {
          // Draw school logo (if available)
          if (school.picture?.path) {
            const logoImage = new Image();
            logoImage.crossOrigin = "anonymous";
            logoImage.onload = () => {
              // Draw logo at top
              const logoSize = 80;
              ctx.drawImage(logoImage, (canvas.width - logoSize) / 2, 40, logoSize, logoSize);

              // Draw school name
              ctx.fillStyle = "#1f2937";
              ctx.font = "bold 24px Arial, sans-serif";
              ctx.textAlign = "center";
              ctx.fillText(school.school_name || "School Name", canvas.width / 2, 150);

              // Draw QR code
              const qrSize = 300;
              const qrX = (canvas.width - qrSize) / 2;
              const qrY = 200;
              ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

              // Draw instructions
              ctx.fillStyle = "#374151";
              ctx.font = "16px Arial, sans-serif";
              ctx.textAlign = "center";

              const instructions = [
                "Teacher Attendance QR Code",
                "",
                "How to use:",
                "• Teachers scan this QR code with their mobile device",
                "• QR code is valid for teacher attendance only",
              ];

              let yPos = qrY + qrSize + 40;
              instructions.forEach((line) => {
                if (line === "") {
                  yPos += 10; // Empty line spacing
                } else {
                  ctx.fillText(line, canvas.width / 2, yPos);
                  yPos += 25;
                }
              });

              // Draw generation info
              ctx.fillStyle = "#6b7280";
              ctx.font = "12px Arial, sans-serif";
              ctx.fillText(
                `Generated: ${format(new Date(), "dd/MM/yyyy hh:mm a")}`,
                canvas.width / 2,
                yPos + 20,
              );
              //ctx.fillText(`School ID: ${schoolId}`, canvas.width / 2, yPos + 40);

              resolve(canvas.toDataURL("image/png"));
            };
            logoImage.onerror = () => {
              // Fallback without logo
              drawWithoutLogo();
            };
            logoImage.src = school.picture.path;
          } else {
            // No logo, draw without it
            drawWithoutLogo();
          }

          function drawWithoutLogo() {
            // Draw school name
            ctx.fillStyle = "#1f2937";
            ctx.font = "bold 28px Arial, sans-serif";
            ctx.textAlign = "center";
            ctx.fillText(school.school_name || "School Name", canvas.width / 2, 80);

            // Draw QR code
            const qrSize = 300;
            const qrX = (canvas.width - qrSize) / 2;
            const qrY = 120;
            ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

            // Draw instructions
            ctx.fillStyle = "#374151";
            ctx.font = "16px Arial, sans-serif";
            ctx.textAlign = "center";

            const instructions = [
              "Teacher Attendance QR Code",
              "",
              "How to use:",
              "• Teachers scan this QR code with their mobile device",
              "• QR code is valid for teacher attendance only",
            ];

            let yPos = qrY + qrSize + 40;
            instructions.forEach((line) => {
              if (line === "") {
                yPos += 10; // Empty line spacing
              } else {
                ctx.fillText(line, canvas.width / 2, yPos);
                yPos += 25;
              }
            });

            // Draw generation info
            ctx.fillStyle = "#6b7280";
            ctx.font = "12px Arial, sans-serif";
            ctx.fillText(
              `Generated: ${format(new Date(), "dd/MM/yyyy hh:mm a")}`,
              canvas.width / 2,
              yPos + 20,
            );
            //ctx.fillText(`School ID: ${schoolId}`, canvas.width / 2, yPos + 40);

            resolve(canvas.toDataURL("image/png"));
          }
        } catch (error) {
          reject(error);
        }
      };

      qrImage.onerror = () => {
        reject(new Error("Failed to load QR code image"));
      };

      qrImage.src = qrCodeUrl;
    });
  };

  // Download branded QR code as image
  const handleDownloadQRCode = async () => {
    if (!qrCodeData?.qrCodeUrl) return;

    try {
      setQrLoading(true);
      const brandedQRCode = brandedQRPreview || (await generateBrandedQRCode());

      if (brandedQRCode) {
        const link = document.createElement("a");
        link.href = brandedQRCode;
        link.download = `${school?.school_name?.replace(/[^a-zA-Z0-9]/g, "_") || "school"}_teacher_attendance_qr_${new Date().toISOString().split("T")[0]}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setQrSuccess("Branded QR code downloaded successfully!");
      }
    } catch (error) {
      console.error("Error generating branded QR code:", error);
      setQrError("Failed to generate branded QR code");

      // Fallback to plain QR code download
      const link = document.createElement("a");
      link.href = qrCodeData.qrCodeUrl;
      link.download = `teacher-attendance-qr-${schoolId}-${new Date().toISOString().split("T")[0]}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      setQrLoading(false);
    }
  };

  // Close QR modal and clear data
  const handleCloseQRModal = () => {
    setShowQRModal(false);
    setQrCodeData(null);
    setBrandedQRPreview(null);
  };

  // Navigate to device approval management page
  const handleManageDeviceRequests = () => {
    if (!schoolId) {
      setError("School ID not found");
      return;
    }
    history.push(`/device-approval/${schoolId}/requests`);
  };

  const summary = useMemo(() => summarize(teacherAttendance), [teacherAttendance]);
  const staffCount = useMemo(
    () => new Set(teacherAttendance.map((row) => row.userId)).size,
    [teacherAttendance],
  );
  const individualSummary = useMemo(() => summarize(individualAttendance), [individualAttendance]);

  const visibleAttendance = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    return teacherAttendance.filter((row) => {
      if (statusFilter !== "all" && statusMeta(row.record?.status).key !== statusFilter) {
        return false;
      }
      if (!query) return true;
      return `${row.firstName || ""} ${row.lastName || ""}`.toLowerCase().includes(query);
    });
  }, [teacherAttendance, searchText, statusFilter]);

  const isSingleDay = filters.from === filters.to;
  const rangeLabel = isSingleDay
    ? format(new Date(filters.from), "EEEE, dd MMM yyyy")
    : `${format(new Date(filters.from), "dd MMM yyyy")} – ${format(new Date(filters.to), "dd MMM yyyy")}`;

  const renderStatusBadge = (status) => {
    const meta = statusMeta(status);
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${meta.badge}`}
      >
        {meta.label}
      </span>
    );
  };

  const renderTime = (value) =>
    value ? (
      <span className="text-sm text-slate-700">{value}</span>
    ) : (
      <span className="text-sm text-slate-300">—</span>
    );

  const columns = [
    {
      name: "Staff",
      selector: (row) => `${row.firstName || ""} ${row.lastName || ""}`,
      cell: (row) => (
        <div className="flex items-center gap-3 py-2">
          <div className="w-8 h-8 rounded-full bg-indigo-50 text-[#3F3DA5] flex items-center justify-center text-xs font-semibold flex-shrink-0">
            {initials(row)}
          </div>
          <span className="font-medium text-slate-800">
            {row.firstName} {row.lastName}
          </span>
        </div>
      ),
      sortable: true,
      minWidth: "200px",
    },
    {
      name: "Date",
      selector: (row) => row.record?.date || "",
      cell: (row) => <span className="text-sm text-slate-600">{formatRecordDate(row.record)}</span>,
      sortable: true,
      minWidth: "150px",
    },
    {
      name: "Status",
      selector: (row) => statusMeta(row.record?.status).label,
      cell: (row) => renderStatusBadge(row.record?.status),
      sortable: true,
      width: "130px",
    },
    {
      name: "Clock in",
      selector: (row) => row.record?.clock_in?.time || "",
      cell: (row) => renderTime(formatTime(row.record?.clock_in)),
      sortable: true,
      width: "120px",
    },
    {
      name: "Clock out",
      selector: (row) => row.record?.clock_out?.time || "",
      cell: (row) => renderTime(formatTime(row.record?.clock_out)),
      sortable: true,
      width: "120px",
    },
    {
      name: "Time on duty",
      cell: (row) => renderTime(formatWorked(row.record)),
      width: "130px",
    },
    {
      name: "",
      cell: (row) => (
        <button
          onClick={() => handleViewIndividualAttendance(row)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#3F3DA5] bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
          title="View this staff member's attendance history"
        >
          <FiList className="w-3.5 h-3.5" />
          History
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "120px",
    },
  ];

  const individualColumns = columns.filter((column) => column.name !== "Staff" && column.name !== "");

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#f8fafc",
        fontSize: "12px",
        fontWeight: 600,
        color: "#475569",
        borderBottomColor: "#e2e8f0",
      },
    },
    rows: {
      style: {
        borderBottomColor: "#f1f5f9",
      },
    },
  };

  const statCards = [
    {
      key: "all",
      label: "Staff",
      value: staffCount,
      hint: isSingleDay ? "with a record today" : "with records in range",
      icon: FiUsers,
      iconClass: "bg-indigo-50 text-[#3F3DA5]",
    },
    {
      key: "present",
      label: "Present",
      value: summary.present,
      hint: "on time",
      icon: FiCheckCircle,
      iconClass: "bg-emerald-50 text-emerald-600",
    },
    {
      key: "late",
      label: "Late",
      value: summary.late,
      hint: "clocked in late",
      icon: FiClock,
      iconClass: "bg-amber-50 text-amber-600",
    },
    {
      key: "absent",
      label: "Absent",
      value: summary.absent,
      hint: "did not clock in",
      icon: FiXCircle,
      iconClass: "bg-rose-50 text-rose-600",
    },
  ];

  const selectClass =
    "rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-[#3F3DA5] focus:ring-2 focus:ring-indigo-100";

  return (
    <div className="m-2 md:m-10 mt-24 p-4 md:p-10 dark:bg-secondary-dark-bg bg-white rounded-3xl min-h-screen">
      {/* Header: title with the main actions, then the attendance tools in one row */}
      <div className="flex flex-col gap-4 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-slate-800">Teacher Attendance</h1>
            <p className="text-sm text-slate-500 mt-1">
              Clock-in records for Term {filters.term}, {filters.session}
            </p>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              onClick={() => setShowSettingsModal(true)}
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              title="Staff lateness and absence settings"
            >
              <FiSettings className="w-4 h-4" />
              Settings
            </button>
            <button
              onClick={handleGenerateQRCode}
              disabled={qrLoading}
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#3F3DA5] rounded-lg hover:bg-[#2E2D7A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              title="QR code staff scan to clock in and out"
            >
              {qrLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
              ) : (
                <BsQrCode className="w-4 h-4" />
              )}
              {qrLoading ? "Generating..." : "Clock-in QR code"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 rounded-xl border border-slate-200 bg-white divide-y sm:divide-y-0 sm:divide-x divide-slate-200 overflow-hidden">
          {[
            {
              key: "attempts",
              label: "Clock attempts",
              hint: "Every scan, including blocked ones",
              icon: FiClock,
              onClick: () => setShowClockAttemptsModal(true),
            },
            {
              key: "penalties",
              label: "Lateness penalties",
              hint: "Deductions and waivers",
              icon: FiAlertCircle,
              onClick: () => setShowPenaltiesModal(true),
            },
            {
              key: "devices",
              label: "Device requests",
              hint: "Approve clock-ins from a new phone",
              icon: FiSmartphone,
              onClick: handleManageDeviceRequests,
            },
          ].map(({ key, label, hint, icon: Icon, onClick }) => (
            <button
              key={key}
              type="button"
              onClick={onClick}
              className="flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 focus:outline-none focus-visible:bg-slate-50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#3F3DA5] transition-colors"
            >
              <span className="w-9 h-9 rounded-lg bg-indigo-50 text-[#3F3DA5] flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4" />
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-slate-800">{label}</span>
                <span className="block text-xs text-slate-500 truncate">{hint}</span>
              </span>
            </button>
          ))}
        </div>
      </div>

      {success && (
        <MobileFriendlyNotification
          type="success"
          message={success}
          onClose={() => setSuccess("")}
          autoClose={3000}
        />
      )}
      {error && (
        <MobileFriendlyNotification
          type="error"
          message={typeof error === "string" ? error : error?.message || "Something went wrong."}
          onClose={() => setError("")}
          autoClose={5000}
        />
      )}
      {qrSuccess && (
        <MobileFriendlyNotification
          type="success"
          message={qrSuccess}
          onClose={() => setQrSuccess("")}
          autoClose={5000}
        />
      )}
      {qrError && (
        <MobileFriendlyNotification
          type="error"
          message={qrError}
          onClose={() => setQrError("")}
          autoClose={5000}
        />
      )}

      {/* Summary — click a card to filter the table */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {statCards.map((card) => {
          const Icon = card.icon;
          const active = statusFilter === card.key;
          return (
            <button
              key={card.key}
              type="button"
              onClick={() => setStatusFilter(card.key)}
              className={`text-left rounded-xl border p-4 transition-colors ${
                active
                  ? "border-[#3F3DA5] bg-indigo-50/40 ring-1 ring-[#3F3DA5]"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-xs font-medium text-slate-500">{card.label}</p>
                  <p className="text-2xl font-bold text-slate-800 mt-1">
                    {loading ? "–" : card.value}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-0.5">{card.hint}</p>
                </div>
                <div
                  className={`w-9 h-9 rounded-lg flex items-center justify-center ${card.iconClass}`}
                >
                  <Icon className="w-4 h-4" />
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Records */}
      <div className="rounded-2xl border border-slate-200 overflow-hidden">
        {/* Filters */}
        <div className="p-4 border-b border-slate-200 bg-slate-50/60 space-y-3">
          <div className="flex flex-col xl:flex-row xl:items-end gap-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 flex-1">
              <label className="block">
                <span className="block text-xs font-medium text-slate-500 mb-1">Session</span>
                <select
                  value={filters.session}
                  onChange={(e) => handleDirectFilterChange("session", e.target.value)}
                  className={`w-full ${selectClass}`}
                >
                  {schoolSessions.map((session) => (
                    <option key={session.value} value={session.value}>
                      {session.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-slate-500 mb-1">Term</span>
                <select
                  value={filters.term}
                  onChange={(e) => handleDirectFilterChange("term", e.target.value)}
                  className={`w-full ${selectClass}`}
                >
                  <option value="1">1st Term</option>
                  <option value="2">2nd Term</option>
                  <option value="3">3rd Term</option>
                </select>
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-slate-500 mb-1">From</span>
                <input
                  type="date"
                  value={filters.from}
                  onChange={(e) => e.target.value && handleDirectFilterChange("from", e.target.value)}
                  className={`w-full ${selectClass}`}
                />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-slate-500 mb-1">To</span>
                <input
                  type="date"
                  value={filters.to}
                  onChange={(e) => e.target.value && handleDirectFilterChange("to", e.target.value)}
                  className={`w-full ${selectClass}`}
                />
              </label>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handleShowToday}
                className="px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Today
              </button>
              <button
                onClick={() => fetchTeacherAttendance()}
                disabled={loading}
                className="p-2.5 text-slate-500 hover:text-[#3F3DA5] bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
                title="Refresh"
              >
                <FiRefreshCw className={`w-4 h-4 ${loading ? "animate-spin text-[#3F3DA5]" : ""}`} />
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <p className="text-sm text-slate-600">
              <FiCalendar className="inline w-4 h-4 mr-1.5 -mt-0.5 text-slate-400" />
              {rangeLabel}
              {statusFilter !== "all" && (
                <button
                  onClick={() => setStatusFilter("all")}
                  className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-[#3F3DA5] bg-indigo-50 rounded-full hover:bg-indigo-100"
                >
                  {statusMeta(
                    Object.keys(STATUS_META).find((k) => STATUS_META[k].key === statusFilter),
                  ).label}{" "}
                  only
                  <FiX className="w-3 h-3" />
                </button>
              )}
            </p>
            <div className="relative w-full sm:w-64">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search staff..."
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 bg-white focus:border-[#3F3DA5] focus:ring-2 focus:ring-indigo-100"
              />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <DataTable
            data={visibleAttendance}
            columns={columns}
            keyField="uniqueId"
            pagination
            paginationPerPage={15}
            paginationRowsPerPageOptions={[15, 30, 50]}
            customStyles={customStyles}
            progressPending={loading}
            progressComponent={<SkeletonTable rows={5} columns={7} />}
            highlightOnHover
            noDataComponent={
              <div className="py-14 text-center">
                <FiCalendar className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                <p className="text-sm font-medium text-slate-700">No attendance records</p>
                <p className="text-xs text-slate-500 mt-1">
                  {searchText || statusFilter !== "all"
                    ? "Nothing matches your search or filter."
                    : "No staff clocked in during this period."}
                </p>
              </div>
            }
          />
        </div>
      </div>

      {/* Individual Staff Attendance Modal */}
      {showIndividualModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-indigo-50 text-[#3F3DA5] flex items-center justify-center text-sm font-semibold">
                  {selectedTeacher ? initials(selectedTeacher) : "?"}
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">
                    {selectedTeacher?.firstName} {selectedTeacher?.lastName}
                  </h2>
                  <p className="text-xs text-slate-500">Attendance history</p>
                </div>
              </div>
              <button
                onClick={handleCloseIndividualModal}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/60">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
                <label className="block">
                  <span className="block text-xs font-medium text-slate-500 mb-1">Session</span>
                  <select
                    value={individualFilters.session}
                    onChange={(e) => handleIndividualFilterChange("session", e.target.value)}
                    className={`w-full ${selectClass}`}
                  >
                    {schoolSessions.map((session) => (
                      <option key={session.value} value={session.value}>
                        {session.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-slate-500 mb-1">Term</span>
                  <select
                    value={individualFilters.term}
                    onChange={(e) => handleIndividualFilterChange("term", e.target.value)}
                    className={`w-full ${selectClass}`}
                  >
                    <option value="1">1st Term</option>
                    <option value="2">2nd Term</option>
                    <option value="3">3rd Term</option>
                  </select>
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-slate-500 mb-1">From</span>
                  <input
                    type="date"
                    value={individualFilters.from}
                    onChange={(e) => handleIndividualFilterChange("from", e.target.value)}
                    className={`w-full ${selectClass}`}
                  />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-slate-500 mb-1">To</span>
                  <input
                    type="date"
                    value={individualFilters.to}
                    onChange={(e) => handleIndividualFilterChange("to", e.target.value)}
                    className={`w-full ${selectClass}`}
                  />
                </label>
                <button
                  onClick={handleApplyIndividualFilters}
                  className="col-span-2 md:col-span-1 px-4 py-2 text-sm font-medium text-white bg-[#3F3DA5] rounded-lg hover:bg-[#2E2D7A] transition-colors"
                >
                  Apply
                </button>
              </div>

              <div className="flex flex-wrap gap-2 mt-3">
                {[
                  { key: "present", status: 1 },
                  { key: "late", status: 2 },
                  { key: "absent", status: 0 },
                ].map(({ key, status }) => (
                  <span
                    key={key}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ring-inset ${statusMeta(status).badge}`}
                  >
                    {statusMeta(status).label}
                    <span className="font-bold">{individualLoading ? "–" : individualSummary[key]}</span>
                  </span>
                ))}
              </div>
            </div>

            <div className="overflow-y-auto">
              <DataTable
                data={individualAttendance}
                columns={individualColumns}
                keyField="uniqueId"
                pagination
                paginationPerPage={15}
                paginationRowsPerPageOptions={[15, 30, 50]}
                customStyles={customStyles}
                progressPending={individualLoading}
                progressComponent={<SkeletonTable rows={5} columns={5} />}
                highlightOnHover
                noDataComponent={
                  <div className="py-12 text-center">
                    <FiCalendar className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                    <p className="text-sm text-slate-500">No records for the selected period</p>
                  </div>
                }
              />
            </div>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {showQRModal && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">Clock-in QR code</h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  Print this and place it where staff arrive. They scan it to clock in and out.
                </p>
              </div>
              <button
                onClick={handleCloseQRModal}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 text-center">
              {qrCodeData?.qrCodeUrl ? (
                <>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    {brandedQRPreview ? (
                      <img
                        src={brandedQRPreview}
                        alt="Branded clock-in QR code"
                        className="mx-auto max-h-96 w-auto"
                      />
                    ) : (
                      <img
                        src={qrCodeData.qrCodeUrl}
                        alt="Clock-in QR code"
                        className="mx-auto w-56 h-56"
                      />
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    Generated {format(new Date(qrCodeData.generatedAt), "dd MMM yyyy, hh:mm a")}
                  </p>
                </>
              ) : (
                <div className="py-8">
                  <FiSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-sm text-slate-500">QR code not available</p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button
                onClick={handleCloseQRModal}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                Close
              </button>
              <button
                onClick={handleDownloadQRCode}
                disabled={qrLoading || !qrCodeData?.qrCodeUrl}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#3F3DA5] rounded-lg hover:bg-[#2E2D7A] disabled:opacity-50"
              >
                {qrLoading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                ) : (
                  <FiDownload className="w-4 h-4" />
                )}
                Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clock Attempts Modal */}
      <ClockAttemptsModal
        isOpen={showClockAttemptsModal}
        onClose={() => setShowClockAttemptsModal(false)}
        school={school}
        refreshSchool={refreshSchool}
      />

      {/* Staff attendance settings (moved here from Gate Monitoring) */}
      <StaffClockInSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        schoolId={schoolId}
        refreshSchool={refreshSchool}
      />

      {/* Staff Lateness & Absence Penalties Modal */}
      <StaffLatenessPenaltiesModal
        isOpen={showPenaltiesModal}
        onClose={() => setShowPenaltiesModal(false)}
        school={school}
        refreshSchool={refreshSchool}
      />
    </div>
  );
};

export default TeacherAttendance;
